import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import RefreshIcon from '@mui/icons-material/Refresh';
import { ApiError, PosPrintStation, posPrintStations } from '../api';
import { setStationId, stationId } from './station';
import { COLORS } from './theme';

/** Sentinel for the "type it myself" option. Not a station id anyone
 *  would use, and kept out of the saved value. */
const CUSTOM = ' custom';
/** Sentinel for "this till doesn't print". */
const NONE = '';

type Props = {
  idToken: string;
  /** Read-only context, already fetched by the register. */
  testMode: boolean;
  refundWindowDays: number;
  onToast: (message: string) => void;
};

/** Till settings.
 *
 *  Only one setting so far, and it's the one that can't have a sensible
 *  default: which printer station this machine sends receipts to. It has
 *  to match a station id in the server's config *and* the `station_id`
 *  in that counter PC's own config — three files, one string — so this
 *  offers the list rather than asking anyone to type it twice.
 *
 *  Everything else here is read-only, and is here because "why is this
 *  till behaving oddly" is usually answered by one of these three
 *  values. */
const SettingsPanel: React.FC<Props> = ({
  idToken,
  testMode,
  refundWindowDays,
  onToast,
}) => {
  const [saved, setSaved] = useState(stationId());
  const [choice, setChoice] = useState(stationId());
  const [typed, setTyped] = useState('');
  const [stations, setStations] = useState<PosPrintStation[]>([]);
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await posPrintStations(idToken);
      setStations(res.stations);
      setEnabled(res.enabled);
      // A station saved on this machine that the server has never heard
      // of still has to be selectable, or opening this tab would
      // silently re-point the till at something else.
      const current = stationId();
      if (current !== '' && !res.stations.some((s) => s.station_id === current)) {
        setChoice(CUSTOM);
        setTyped(current);
      }
    } catch (e) {
      setError(
        e instanceof ApiError
          ? `Couldn't load stations — API error ${e.status}`
          : "Couldn't load stations — check the network",
      );
    } finally {
      setLoading(false);
    }
  }, [idToken]);

  useEffect(() => {
    void load();
  }, [load]);

  const pending = choice === CUSTOM ? typed.trim() : choice;
  const dirty = pending !== saved;

  const save = () => {
    setStationId(pending);
    setSaved(pending);
    onToast(
      pending === ''
        ? 'Station cleared — this till will not print receipts.'
        : `Receipts from this till will print at "${pending}".`,
    );
  };

  return (
    <Box sx={{ p: 2, overflowY: 'auto' }}>
      <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', mb: 0.5 }}>
        Receipt printer
      </Typography>
      <Typography sx={{ color: COLORS.textMuted, mb: 2, fontSize: '0.95rem' }}>
        Which printer this till sends receipts to. Saved on this machine
        only — each register is set separately.
      </Typography>

      {!enabled && !loading && (
        <Box
          sx={{
            backgroundColor: COLORS.warnBg,
            border: `1px solid ${COLORS.warn}`,
            color: COLORS.warnText,
            borderRadius: 2,
            px: 2,
            py: 1.5,
            mb: 2,
          }}
        >
          Receipt printing is switched off on the server, so nothing will
          print no matter what is selected here.
        </Box>
      )}

      {error !== '' && (
        <Box
          sx={{
            backgroundColor: COLORS.dangerBg,
            border: `1px solid ${COLORS.danger}`,
            color: COLORS.danger,
            borderRadius: 2,
            px: 2,
            py: 1.5,
            mb: 2,
          }}
        >
          {error}
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1 }}>
        <FormControl fullWidth>
          <InputLabel id="station-label">Station</InputLabel>
          <Select
            labelId="station-label"
            label="Station"
            value={choice}
            onChange={(e) => setChoice(e.target.value)}
            disabled={loading}
          >
            <MenuItem value={NONE}>
              <em>None — don't print receipts</em>
            </MenuItem>
            {stations.map((s) => (
              <MenuItem key={s.station_id} value={s.station_id}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    width: '100%',
                  }}
                >
                  <PrintIcon
                    fontSize="small"
                    sx={{
                      color: s.connected ? COLORS.accent : COLORS.textMuted,
                    }}
                  />
                  <span>{s.station_id}</span>
                  <StationChips station={s} />
                </Box>
              </MenuItem>
            ))}
            <MenuItem value={CUSTOM}>
              <em>Type a station id…</em>
            </MenuItem>
          </Select>
        </FormControl>
        <Button
          onClick={() => void load()}
          disabled={loading}
          sx={{ minWidth: 0, px: 2, height: 56 }}
          title="Re-check which printers are connected"
        >
          {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
        </Button>
      </Box>

      {choice === CUSTOM && (
        <TextField
          fullWidth
          label="Station id"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          helperText="Must match the station_id in that counter PC's config.toml exactly."
          sx={{ mb: 1 }}
        />
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
        <Button
          variant="contained"
          onClick={save}
          disabled={!dirty || (choice === CUSTOM && typed.trim() === '')}
        >
          Save
        </Button>
        <Typography sx={{ color: COLORS.textMuted }}>
          {saved === '' ? 'No station set' : `Currently: ${saved}`}
        </Typography>
      </Box>

      {!loading && stations.length === 0 && error === '' && (
        <Typography sx={{ color: COLORS.textMuted, mt: 2, fontSize: '0.9rem' }}>
          No stations are configured or connected. A counter's print
          service registers itself when it starts, so this list fills in
          on its own once one is running.
        </Typography>
      )}

      <Divider sx={{ my: 3 }} />

      <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', mb: 1 }}>
        This till
      </Typography>
      <InfoRow label="Server" value={process.env.REACT_APP_API_URL ?? 'localhost:9910'} />
      <InfoRow
        label="Mode"
        value={testMode ? 'TEST — sales are recorded as test data' : 'Live'}
      />
      <InfoRow
        label="Refund window"
        value={refundWindowDays === 0 ? 'No limit' : `${refundWindowDays} days`}
      />
      <Typography sx={{ color: COLORS.textMuted, mt: 2, fontSize: '0.85rem' }}>
        These three come from the server and can't be changed here.
      </Typography>
    </Box>
  );
};

/** Status at a glance: is it on, and is its printer actually ready. */
const StationChips: React.FC<{ station: PosPrintStation }> = ({ station }) => (
  <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto' }}>
    {!station.configured && (
      <Chip
        size="small"
        label="not in server config"
        sx={{ backgroundColor: COLORS.warnBg, color: COLORS.warnText }}
      />
    )}
    {station.connected ? (
      station.printer_ready === false ? (
        <Chip
          size="small"
          label="printer not ready"
          sx={{ backgroundColor: COLORS.warnBg, color: COLORS.warnText }}
        />
      ) : (
        <Chip
          size="small"
          label="ready"
          sx={{ backgroundColor: '#e6f4ec', color: COLORS.accentDark }}
        />
      )
    ) : (
      <Chip size="small" label="offline" />
    )}
  </Box>
);

const InfoRow: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
    <Typography sx={{ color: COLORS.textMuted }}>{label}</Typography>
    <Typography sx={{ fontWeight: 600, textAlign: 'right', ml: 2 }}>
      {value}
    </Typography>
  </Box>
);

export default SettingsPanel;
